import { Component, OnInit } from '@angular/core';
import { EstablishmentModel } from 'src/app/core/models/establishment.model';
import { EstablishmentService } from './establishment.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { EstablishmentCreateUpdateDeleteComponent } from './modals/establishment-create-update-delete/establishment-create-update-delete.component';
import { FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { CrudModeEnum } from 'src/app/core/base/enum/crud-mode.enum';
import { Router } from '@angular/router';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { AuthService } from '../../../../core/services/auth.service';
import {
  EstablishmentStatusEnum,
  EstablishmentStatusTranslation,
} from '../../../../core/enums/establishment-status.enum';
import { DependenciesService } from '../../../../core/services/dependencies.service';
import { FileSaverService } from 'ngx-filesaver';
import { b64ToBlob } from '../../../../core/helpers/b64ToBlob.helper';
import { ContentTypeEnum } from '../../../../core/base/enum/content-type.enum';
import {
  EstablishmentTypeEnum,
  establishmentTypeFrenchTranslation,
} from '../../../../core/enums/establishment-type.enum';

@Component({
  selector: 'vex-establishment',
  templateUrl: './establishment.component.html',
  styleUrls: ['./establishment.component.scss'],
})
export class EstablishmentComponent extends DataTableBase<EstablishmentModel> implements OnInit {
  feisColumns: Array<FeiColumn> = [
    { label: 'Identifiant Technique', column: 'technicalIdentifier' },
    { label: 'Dénomination Établissement', column: 'label' },
    { label: 'N° Siren', column: 'sirenNumber' },
    { label: 'Identifiant Administratif', column: 'administrativeIdentifier' },
    { label: 'Code ape', column: 'ape' },
    { label: 'Code TVA', column: 'tva' },
    { label: "Type d'établissement", column: 'establishmentType' },
    {
      label: 'Statut',
      column: 'status',
      propertyDisplay: 'status',
    },
    {
      label: '',
      column: 'actions',
      canUpdate: false,
      canArchive: this.authService.hasPermission(ENUM_PERMISSIONS.DELETE_ESTABLISHMENT),
    },
  ];
  ENUM_PERMISSIONS = ENUM_PERMISSIONS;

  canCreatePermissions = false;
  canShowArchivePermissions = false;
  sortField = 'createdAt';
  sortOrder = 'desc';

  constructor(
    public service: EstablishmentService,
    private dialog: MatDialog,
    private router: Router,
    public authService: AuthService,
    private dependencyService: DependenciesService<EstablishmentModel>,
    private fileSaver: FileSaverService,
  ) {
    super();
    this.displayedColumns = this.feisColumns.map(x => x.column);
  }

  ngOnInit() {
    super.ngOnInit();

    this.authService.getCurrentUserPermissions$().subscribe(permissions => {
      this.canCreatePermissions = permissions.includes(ENUM_PERMISSIONS.CREATE_ESTABLISHMENT);
      this.canShowArchivePermissions = permissions.includes(ENUM_PERMISSIONS.READ_ESTABLISHMENT);
    });
  }

  initData() {
    super.initData();
    this.requiredFields = [
      'label',
      'sirenNumber',
      'siretNumber',
      'dunsNumber',
      'administrativeIdentifier',
      'ape',
      'tva',
      'establishmentType',
      'status',
      'technicalIdentifier',
      'createdAt',
    ];
    this.filters = {
      ...this.filters,
    };
  }

  protected override transformData(data: any[]): any[] {
    return data.map((establishment: EstablishmentModel) => {
      establishment.status = EstablishmentStatusTranslation.translate(establishment.status as EstablishmentStatusEnum);
      establishment.establishmentType = establishmentTypeFrenchTranslation(
        establishment.establishmentType as EstablishmentTypeEnum,
      );
      return establishment;
    });
  }

  createItem(): void {
    this.dialog
      .open(EstablishmentCreateUpdateDeleteComponent)
      .afterClosed()
      .subscribe((establishment: EstablishmentModel) => {
        if (establishment) {
          if (establishment._id) {
            this.router.navigateByUrl('/management/establishment/' + establishment._id).then();
          } else {
            this.initData();
          }
        }
      });
  }

  importItem(): void {
    this.dialog
      .open(EstablishmentCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Import,
        },
      })
      .afterClosed()
      .subscribe((establishment: EstablishmentModel[]) => {
        if (establishment) {
          this.pageIndex = 1;
          this.initData();
        }
      });
  }

  exportAddresses() {
    this.service.exportEstablishmentAddresses().subscribe((x: any) => {
      const b = b64ToBlob(x.data.b64, ContentTypeEnum.excel);
      this.fileSaver.save(b, x.data.fileName);
    });
  }

  updateItem(element: EstablishmentModel): void {
    this.dialog
      .open(EstablishmentCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((establishment: EstablishmentModel) => {
        if (establishment) {
          this.initData();
        }
      });
  }

  deleteItem(element: EstablishmentModel) {
    this.dialog
      .open(EstablishmentCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.label}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe((establishment: EstablishmentModel) => {
        if (establishment) {
          this.initData();
        }
      });
  }

  async archiveItem(element: EstablishmentModel) {
    await this.dependencyService
      .setService(this.service)
      .setModuleName('établissement')
      .setModuleNameWithArticle('un établissement')
      .setModuleNameTitleWithArticle("de l'établissement")
      .setIsStepByStep(true)
      .checkDependencies(element, this.initData.bind(this));
  }
}
